import React, {useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {Modal} from 'react-bootstrap'
import {useAuth} from '../../../modules/auth'
import {shouldDisplayFonctionnalite} from '../../../../components/helpers'
import {LoadingData} from '../../../../components/LoadingData'
import {getClientAdresses} from '../../../../components/requests'
import {
  ModeleSavAdresse,
  ModeleSavDossier,
  ModeleSavLigne,
  SavDetailResponse,
} from '../../../../components/sav/Models'
import {savErreur, updateSavDossier} from '../../../../components/sav/requests'
import {
  formatSavDate,
  formatSavMontant,
  nomSavUtilisateur,
  SAV_ORIGINES,
} from '../../../../components/sav/libelles'
import SavDossierHeader from '../components/SavDossierHeader'
import SavFilModifications from '../components/SavFilModifications'
import SavDocuments, {SavAjoutDocument} from '../components/SavDocuments'
import SavGarantie from '../components/SavGarantie'
import SavDemandeRemplacement from '../components/SavDemandeRemplacement'
import {useSavDossier} from '../components/useSavDossier'
import {useSavReferentiels} from '../components/useSavReferentiels'
import SavControleForm from '../components/SavControleForm'
import {SavInformerClientModal} from '../components/SavInformerClient'

const ouiNon = (valeur?: boolean | null) =>
  valeur === null || valeur === undefined ? '—' : valeur ? 'Oui' : 'Non'

const adresseTexte = (a?: ModeleSavAdresse | null) =>
  a
    ? [a.adresse, a.complementAdresse, `${a.codePostal ?? ''} ${a.ville ?? ''}`.trim()]
        .filter(Boolean)
        .join(', ')
    : ''

// Section d'une pièce : informations, résultat du contrôle labo ou contrôle à saisir
const SavLigneSection = ({
  ligne,
  detail,
  controle,
}: {
  ligne: ModeleSavLigne
  detail: SavDetailResponse
  // Formulaire de contrôle, pour un contrôleur quand la pièce est au labo
  controle?: React.ReactNode
}) => {
  const photos = detail.dossier.documents.filter((d) => d.ligne?.id === ligne.id)
  return (
    <div className='border rounded p-5 mb-5'>
      <div className='d-flex justify-content-between flex-wrap gap-2 mb-3'>
        <div>
          <div className='fs-5 fw-bolder'>
            {(ligne.pieceEntre?.code ?? ligne.codePieceExterne) &&
              `${ligne.pieceEntre?.code ?? ligne.codePieceExterne} - `}
            {ligne.libellePiece}
          </div>
          <div className='text-muted fs-7'>
            {ligne.referenceConstructeur && `Réf. OE ${ligne.referenceConstructeur}`}
            {ligne.immatriculation && ` · ${ligne.immatriculation}`}
          </div>
        </div>
        <span
          className={`badge align-self-start ${
            ligne.pieceEntre ? 'badge-light-success' : 'badge-light-warning'
          }`}
        >
          {ligne.pieceEntre ? 'Pièce reconnue dans KOENA' : 'Pièce hors KOENA'}
        </span>
      </div>
      <div className='row g-3 mb-4'>
        <div className='col-md-3'>
          <div className='text-muted fs-7'>Motif</div>
          <div className='fw-semibold'>{ligne.motif?.libelle}</div>
        </div>
        <div className='col-md-3'>
          <div className='text-muted fs-7'>Prix de vente HT</div>
          <div className='fw-semibold'>
            {formatSavMontant(ligne.prixVenteHt)}{' '}
            <span className='text-muted fs-8'>({ligne.prixVerifie ? 'vérifié' : 'déclaré'})</span>
          </div>
        </div>
        <div className='col-md-3'>
          <div className='text-muted fs-7'>Garantie</div>
          <SavGarantie ligne={ligne} declarative={detail.garantieDeclarative} />
        </div>
        <div className='col-md-3'>
          <div className='text-muted fs-7'>Retour requis</div>
          <div className='fw-semibold'>{ouiNon(ligne.retourRequis)}</div>
        </div>
      </div>
      {!ligne.prixVerifie && (
        <div className='alert alert-warning py-2'>
          Prix non vérifié : le montant accordé devra être validé par le responsable SAV.
        </div>
      )}
      {ligne.dateControle ? (
        <div className='bg-light rounded p-4'>
          <div className='fw-bold mb-2'>
            Contrôle du {formatSavDate(ligne.dateControle, true)} par{' '}
            {nomSavUtilisateur(ligne.utilisateurControle)}
          </div>
          <div className='row g-2'>
            <div className='col-md-3'>Lavage : {ouiNon(ligne.lavageEffectue)}</div>
            <div className='col-md-3'>État : {ligne.etatPieceConstate?.libelle ?? '—'}</div>
            <div className='col-md-3'>
              Réclamation :{' '}
              <span
                className={ligne.controleFonde ? 'text-success fw-bold' : 'text-danger fw-bold'}
              >
                {ligne.controleFonde ? 'fondée' : 'non fondée'}
              </span>
            </div>
            <div className='col-md-3'>
              Réutilisable : {ouiNon(ligne.pieceReutilisable)}
              {ligne.pieceReutilisable && ligne.destination?.comportement === 'REMISE_EN_STOCK' && (
                <div className='text-muted fs-8'>remise en stock dans ATEMO par la Logistique</div>
              )}
            </div>
            <div className='col-md-6'>Destination : {ligne.destination?.libelle ?? '—'}</div>
            {ligne.commentaireControle && (
              <div className='col-12 text-gray-700'>{ligne.commentaireControle}</div>
            )}
          </div>
        </div>
      ) : (
        controle ??
        (ligne.retourRequis && (
          <div className='border border-dashed rounded p-4 text-muted'>Contrôle labo à saisir.</div>
        ))
      )}
      {ligne.montantAccordeHt && (
        <div className='mt-3 fw-bold'>
          Montant accordé : {formatSavMontant(ligne.montantAccordeHt)} HT
        </div>
      )}
      {photos.length > 0 && (
        <div className='mt-3'>
          <SavDocuments documents={photos} />
        </div>
      )}
    </div>
  )
}

// Choix de l'adresse de réexpédition parmi les adresses du client
const SavAdresseModal = ({
  dossier,
  onClose,
  onSaved,
}: {
  dossier: ModeleSavDossier
  onClose: () => void
  onSaved: (d: SavDetailResponse) => void
}) => {
  const [adresses, setAdresses] = useState<Array<ModeleSavAdresse> | null>(null)
  const [choix, setChoix] = useState(dossier.clientAdresseRenvoi?.id ?? 0)

  React.useEffect(() => {
    getClientAdresses(dossier.client.id)
      .then((r: any) => setAdresses(r?.data ?? []))
      .catch((e) => toast.error(savErreur(e)))
  }, [dossier.client.id])

  const enregistrer = () =>
    updateSavDossier(dossier.id, {clientAdresseRenvoi: choix || null})
      .then(onSaved)
      .catch((e) => toast.error(savErreur(e)))

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Adresse de renvoi</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {adresses === null ? (
          <LoadingData />
        ) : adresses.length ? (
          adresses.map((a) => (
            <label key={a.id} className='form-check mb-3'>
              <input
                type='radio'
                className='form-check-input'
                checked={choix === a.id}
                onChange={() => setChoix(a.id)}
              />
              <span className='form-check-label'>
                <span className='fw-bold'>{a.libelle}</span>
                <div className='text-muted'>{adresseTexte(a)}</div>
              </span>
            </label>
          ))
        ) : (
          <div className='text-muted'>
            Aucune adresse sur la fiche client : ajoutez-la depuis la fiche client.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className='btn btn-light' onClick={onClose}>
          Annuler
        </button>
        <button className='btn btn-success' onClick={enregistrer} disabled={!choix}>
          Enregistrer
        </button>
      </Modal.Footer>
    </Modal>
  )
}

// Écran 7 : fiche dossier (pièces, vente d'origine, retour, adresse de renvoi)
const SavFichePage = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const {detail, erreur, maj, messagePropose, fermerMessage} = useSavDossier(id)
  const referentiels = useSavReferentiels()
  const [adresse, setAdresse] = useState(false)

  if (erreur) return <div className='alert alert-danger'>{erreur}</div>
  if (!detail) return <LoadingData />
  const {dossier} = detail
  const modifiable =
    !dossier.statut.final && shouldDisplayFonctionnalite(currentUser, 'SAV_QUALIFIER')
  // Écran 7 : formulaire de contrôle identique à l'écran 6 ; chaque pièce se valide séparément
  const auLabo =
    ['RECU', 'EN_CONTROLE'].includes(dossier.statut.code) &&
    shouldDisplayFonctionnalite(currentUser, 'SAV_CONTROLER')
  const aControler = dossier.lignes.some((l) => l.retourRequis)
    ? dossier.lignes.filter((l) => l.retourRequis)
    : dossier.lignes

  const vente = (() => {
    if (dossier.commande)
      return {
        titre: `Commande KOENA · ${dossier.commande.numCommande}`,
        date: dossier.commande.date,
      }
    if (dossier.catalogueCommande)
      return {
        titre: `Catalogue B2B · ${dossier.catalogueCommande.numCommande}`,
        date: dossier.catalogueCommande.date,
      }
    const ref = dossier.refFactureExterne
      ? `facture ${dossier.refFactureExterne}`
      : dossier.refCommandeExterne
      ? `commande ${dossier.refCommandeExterne}`
      : dossier.refTicketOdoo
      ? `ticket ${dossier.refTicketOdoo}`
      : ''
    return {
      titre: `${SAV_ORIGINES[dossier.origine]}${ref ? ` · ${ref}` : ''}${
        dossier.plateforme ? ` (${dossier.plateforme})` : ''
      }`,
      date: dossier.dateAchat,
    }
  })()

  return (
    <>
      <SavDossierHeader
        detail={detail}
        onRecharger={maj}
        ac10={{etape: 'Retour SAV', acteur: 'Équipe SAV', delai: 'J + 1'}}
        action={
          dossier.statut.code === 'OUVERT' ? (
            <button
              className='btn btn-sm btn-success'
              onClick={() => navigate(`/sav/dossiers/${dossier.id}/qualification`)}
            >
              Qualifier le dossier
            </button>
          ) : ['A_SOLDER', 'COMMANDE'].includes(dossier.statut.code) ? (
            <button
              className='btn btn-sm btn-success'
              onClick={() => navigate(`/sav/dossiers/${dossier.id}/solution`)}
            >
              Solution et clôture
            </button>
          ) : undefined
        }
      />

      <div className='row g-5'>
        <div className='col-lg-8'>
          <div className='card'>
            <div className='card-header'>
              <h3 className='card-title'>Pièces ({dossier.lignes.length})</h3>
              <div className='card-toolbar'>
                <SavAjoutDocument
                  dossierId={dossier.id}
                  type='PHOTO_CLIENT'
                  libelle='Ajouter des photos client'
                  onAjoute={maj}
                />
              </div>
            </div>
            <div className='card-body'>
              {dossier.lignes.map((l) => (
                <SavLigneSection
                  key={l.id}
                  ligne={l}
                  detail={detail}
                  controle={
                    auLabo && referentiels && !l.dateControle && aControler.includes(l) ? (
                      <SavControleForm
                        dossierId={dossier.id}
                        ligne={l}
                        referentiels={referentiels}
                        onMaj={maj}
                        onValidee={maj}
                      />
                    ) : undefined
                  }
                />
              ))}
            </div>
          </div>
        </div>

        <div className='col-lg-4'>
          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>Vente d'origine</h3>
            </div>
            <div className='card-body'>
              <div className='fw-bold'>{vente.titre}</div>
              <div className='text-muted'>
                Date d'achat : {vente.date ? formatSavDate(vente.date) : '—'} (
                {dossier.dateAchatVerifiee ? 'vérifiée' : 'déclarée'})
              </div>
              {dossier.clientHistoriqueFacturation && (
                <div className='text-muted fs-7 mt-1'>
                  Rattachée à l'historique de facturation :{' '}
                  {dossier.clientHistoriqueFacturation.numero} (
                  {formatSavMontant(dossier.clientHistoriqueFacturation.montant)})
                </div>
              )}
            </div>
          </div>

          {dossier.clientDemandeRemplacement && (
            <div className='card mb-5'>
              <div className='card-header'>
                <h3 className='card-title'>Échange télévente</h3>
              </div>
              <div className='card-body'>
                <SavDemandeRemplacement dossier={dossier} />
              </div>
            </div>
          )}

          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>Retour</h3>
            </div>
            <div className='card-body'>
              {dossier.retours.length ? (
                dossier.retours.map((r) => (
                  <div key={r.id} className='mb-4'>
                    <div className='fw-bold'>
                      {r.numeroBonRetour
                        ? `Bon de retour ${r.numeroBonRetour}`
                        : 'Dépôt au comptoir'}
                    </div>
                    {r.transporteur && (
                      <div>
                        {r.transporteur.libelle}
                        {r.numeroSuivi && ` · suivi ${r.numeroSuivi}`}
                      </div>
                    )}
                    <div className='text-muted'>
                      {r.dateReception
                        ? `Reçu le ${formatSavDate(r.dateReception, true)} à ${
                            r.siteReception?.nom ?? ''
                          }`
                        : `Attendu à ${r.siteReception?.nom ?? ''}`}
                    </div>
                    {(r.reserveColisAbime ||
                      r.reservePieceManquante ||
                      r.reservePieceDifferente) && (
                      <div className='text-danger fs-7'>
                        Réserves :{' '}
                        {[
                          r.reserveColisAbime && 'colis abîmé',
                          r.reservePieceManquante && 'pièce manquante',
                          r.reservePieceDifferente && 'pièce différente',
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        {r.reserveReception && ` (${r.reserveReception})`}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className='text-muted'>Aucun retour organisé.</div>
              )}
            </div>
          </div>

          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>Adresse de renvoi</h3>
              {modifiable && (
                <div className='card-toolbar'>
                  <button className='btn btn-sm btn-light' onClick={() => setAdresse(true)}>
                    Modifier
                  </button>
                </div>
              )}
            </div>
            <div className='card-body'>
              {dossier.clientAdresseRenvoi ? (
                <>
                  <div className='fw-bold'>{dossier.clientAdresseRenvoi.libelle}</div>
                  <div>{adresseTexte(dossier.clientAdresseRenvoi)}</div>
                </>
              ) : (
                <div className='text-muted'>
                  Aucune adresse : à renseigner si une pièce est renvoyée au client.
                </div>
              )}
            </div>
          </div>

          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>Pièces jointes</h3>
            </div>
            <div className='card-body'>
              <SavDocuments documents={dossier.documents.filter((d) => !d.ligne)} />
            </div>
          </div>
        </div>
      </div>

      <SavFilModifications detail={detail} onRecharger={maj} />

      {messagePropose && (
        <SavInformerClientModal
          dossier={dossier}
          evenement={messagePropose}
          onClose={fermerMessage}
          onTermine={maj}
        />
      )}

      {adresse && (
        <SavAdresseModal
          dossier={dossier}
          onClose={() => setAdresse(false)}
          onSaved={(d) => {
            setAdresse(false)
            maj(d)
          }}
        />
      )}
    </>
  )
}

export default SavFichePage
